import { explorerClassNames } from './KeyValueExplorerRow';

export const HIGHLIGHT_NAME = 'search-highlight';
export const ACTIVE_HIGHLIGHT_NAME = 'search-highlight-active';
export const HIGHLIGHT_SKIP_ATTR = 'data-highlight-skip';

type ActiveMatch = { rowIndex: number; field: 'key' | 'value'; start: number; length: number };

const highlightFilter: NodeFilter = {
  acceptNode(node: Node) {
    if (node.nodeType !== Node.ELEMENT_NODE || !(node instanceof HTMLElement)) return NodeFilter.FILTER_ACCEPT;
    if (node.hasAttribute(HIGHLIGHT_SKIP_ATTR)) return NodeFilter.FILTER_REJECT;
    return NodeFilter.FILTER_SKIP;
  },
};

export function findTextRanges(container: HTMLElement, searchTerm: string, { caseInsensitive = false } = {}): Range[] {
  const treeWalker = document.createTreeWalker(container, NodeFilter.SHOW_ALL, highlightFilter);
  const pattern = new RegExp(searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), caseInsensitive ? 'gi' : 'g');

  const collectTextNodes = (walker: TreeWalker, nodes: Node[] = []): Node[] => {
    const node = walker.nextNode();
    return node ? collectTextNodes(walker, [...nodes, node]) : nodes;
  };

  return collectTextNodes(treeWalker).flatMap(node =>
    (node.textContent?.matchAll(pattern).toArray() ?? []).map(match => {
      const range = new Range();
      range.setStart(node, match.index!);
      range.setEnd(node, match.index! + searchTerm.length);
      return range;
    })
  );
}

function highlightRanges(ranges: Range[], highlightName: string, priority = 0) {
  if (!CSS.highlights) return;
  if (ranges.length > 0) {
    const highlight = new Highlight(...ranges);
    highlight.priority = priority;
    CSS.highlights.set(highlightName, highlight);
  }
}

function findActiveMatchRange(container: HTMLElement, activeMatch?: ActiveMatch): Range[] {
  if (!activeMatch) return [];
  const fieldClassName = activeMatch.field === 'key' ? explorerClassNames.key : explorerClassNames.value;
  const target = container.querySelector(`[data-index="${activeMatch.rowIndex}"] .${fieldClassName}`);
  if (!target?.firstChild || target.firstChild.nodeType !== Node.TEXT_NODE) return [];

  const textNode = target.firstChild;
  const textLength = textNode.textContent?.length ?? 0;
  const start = Math.min(activeMatch.start, textLength);
  const end = Math.min(activeMatch.start + activeMatch.length, textLength);
  if (start >= end) return [];

  const range = new Range();
  range.setStart(textNode, start);
  range.setEnd(textNode, end);
  return [range];
}

export function highlightSearch(container?: HTMLElement | null, search?: string, highlightName = HIGHLIGHT_NAME) {
  CSS.highlights.delete(highlightName);
  const normalizedSearch = search?.trim().toLowerCase();
  if (!container || !normalizedSearch) return;
  const allRanges = findTextRanges(container, normalizedSearch, { caseInsensitive: true });
  highlightRanges(allRanges, highlightName);
}

export function highlightSelection(container?: HTMLElement | null, activeMatch?: ActiveMatch) {
  CSS.highlights.delete(ACTIVE_HIGHLIGHT_NAME);
  if (!container || !activeMatch) return;
  const activeRanges = findActiveMatchRange(container, activeMatch);
  highlightRanges(activeRanges, ACTIVE_HIGHLIGHT_NAME, 1);
}

export function updateHighlights(container?: HTMLElement | null, search?: string, activeMatch?: ActiveMatch) {
  highlightSearch(container, search);
  highlightSelection(container, activeMatch);
}
